import { Component, EventEmitter, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DateTimeUtils } from '@blcloud/bl-common';
import { HttpStatus } from '@blcloud/bl-ng-common';
import { IDmContentInfo, IDmIssueSetting, IDmSnsMsgSetting, IImageUploadInfo, ISnsShopAccountSetting, ITicketSetting } from '@blcloud/bl-datamodel';
import { ContractConst } from '@blcloud/bl-datamodel/const/contract';
import { DmExtractMenuDiv } from '@blcloud/bl-datamodel/enum/approach/dm-extract-menu-div';
import { DmInvitationMediaDiv } from '@blcloud/bl-datamodel/enum/approach/dm-invitation-media-div';
import { DmSnsMsgBodyTypeDiv } from '@blcloud/bl-datamodel/enum/approach/dm-sns-msg-body-type-div';
import { LabelUseCategory } from '@blcloud/bl-datamodel/enum/common/label-use-category';
import { LineMsgTemplateType } from '@blcloud/bl-datamodel/enum/sns/line-msg-template-type';
import { SnsDiv } from '@blcloud/bl-datamodel/enum/sns/sns-div';
import {
  ApproachAnnounceDetailComponent,
  BlImage,
  DmExtractMenuCode,
  DmLineUtil,
  DmSurveyUtil,
  EditMode,
  ISurveyGuideLabelValue,
  LINE_RICH_TEMPLATE_BALLOON_OVERFLOW_MESSAGE,
  SnsImageService,
  SurveySettingResourceDisplayService,
  TicketIssueGuideComponent
} from '@blcloud/bl-ng-share-module';
import {
  AbstractBlModal,
  BlDialogService,
  BlFormGroup,
  BlLoadingService,
  BlModalService,
  BlSelectComponent,
  BlValidatorService,
  FormGroupOption,
  ModalButtonColor,
  ModalButtonSetting,
  ModalButtonType,
  ModalReason,
  ModalSize,
  Rules
} from '@blcloud/bl-ng-ui-component';
import {
  cloneDeep as _cloneDeep,
  delay as _delay,
  filter as _filter,
  find as _find,
  forEach as _forEach,
  isEmpty as _isEmpty,
  isNil as _isNil,
  map as _map,
  remove as _remove,
  some as _some,
  defer as _defer,
  trim as _trim
} from 'lodash';
import { Observable } from 'rxjs/Observable';
import { forkJoin as ObservableForkJoin } from 'rxjs/observable/forkJoin';
import { of as ObservableOf } from 'rxjs/observable/of';
import { map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { SnsSendMessageConfirmModalComponent } from '../sns-send-message-confirm-modal/sns-send-message-confirm-modal.component';
import { ISnsExtractCondition, ISnsSendMessageInfo } from '../sns-send-message-confirm-modal/sns-send-message-confirm-modal.interface';
import { SnsSendMessageContentComponent } from './content/sns-send-message-content.component';
import { DmSnsMsgSettingEx, IDmSnsMsgSettingEx } from './content/sns-send-message-content.interface';
import { SnsSendMessageModalService } from './sns-send-message-modal.service';

/**
 * SNS一斉送信設定モーダル
 * ```
 * content.data: {
 *   // DM案内媒体区分
 *   dmInvitationMediaDiv: string
 * }
 * ```
 */

@Component({
  templateUrl: './sns-send-message-modal.component.html',
  styleUrls: ['./sns-send-message-modal.component.scss'],
  providers: [SnsSendMessageModalService]
})
export class SnsSendMessageModalComponent
  extends AbstractBlModal
  implements OnInit, OnDestroy {

  /** DM案内文設定デフォルト項目 */
  readonly dmContentInfoDefaultItem = {
    code: DmExtractMenuCode.Other,
    name: ''
  };

  private readonly onDestroy$ = new EventEmitter();

  /** 案内文の選択（フォーカス制御用） */
  @ViewChild('dmInvitationInitSelect') dmInvitationInitSelect: BlSelectComponent;

  /** SNS一斉送信設定コンテンツコンポーネント */
  @ViewChild('snsSendMessageContent') snsSendMessageContent: SnsSendMessageContentComponent;

  /** DM案内媒体区分 */
  readonly DmInvitationMediaDiv = DmInvitationMediaDiv;

  /** サービスID定義 */
  readonly ContractConst = ContractConst;

  /** ラベルコードカテゴリ */
  labelUseCategory: string[] = [LabelUseCategory.Customer];

  /** SNS送信メッセージ情報 */
  _snsSendMessageInfo = <ISnsSendMessageInfo>{};

  /** SNS抽出条件情報 */
  _snsExtractCondition = <ISnsExtractCondition>{};

  /** LINE公式アカウントリスト */
  lineOfficialAccountList = [];

  /** SNS店舗アカウント設定情報リスト */
  private snsShopAccountSettingList: ISnsShopAccountSetting[] = [];

  /** 案内方法選択項目リスト */
  contentInfoAlternativeList = [this.dmContentInfoDefaultItem];

  /** DM案内文情報配列 */
  dmContentInfoList: IDmContentInfo[] = [];

  /** DM発行設定情報 */
  dmIssueSetting = <IDmIssueSetting>{};

  /** チケット情報 */
  ticketSettingList: ITicketSetting[] = [{}];

  /** 選択中の送信アンケート */
  _selectedSurvey: ISurveyGuideLabelValue = {};

  /** 案内文の選択非活性フラグ */
  approachSelectDisabledFlg = true;

  /** ボタンタイプ設定 */
  footerButtonType: ModalButtonType = ModalButtonType.OkCancel;

  /** 決定ボタンの設定 */
  okButtonSetting: ModalButtonSetting = { color: ModalButtonColor.Success, label: '送信確認' };

  /** クーポン管理サービスが使用可能か */
  private isAvailableCouponMng = false;

  /** Web予約サービスが有効か */
  isAvailableWebReserveService = false;

  /** アンケート基本サービスが有効か */
  isAvailableSurvey = true;

  /** ログイン組織が他組織のデータ参照可能か(本社拠点か) */
  isAbleToOtherOrgDataRef = false;

  /** 複数のLINE公式アカウントが選択可能か */
  hasMultipleLineAccount = false;

  /** LINE公式アカウント未選択時時の表示用文字列 */
  private readonly UNSELECTED_LINE_OFFICIAL_ACCOUNT_STR = 'LINE公式アカウント未選択';

  /** アンケート未設定エラーメッセージ */
  private readonly SURVEY_NOT_SET_ERR_MSG = 'この案内文を送信する場合は、アンケートを設定してください。';

  /** フォームグループ */
  blFormGroup = new BlFormGroup();

  /** DM案内文情報ルール */
  dmContentInfoRules: Rules[] = [];

  /** リッチ版LINE案内文を設定するか */
  _isSettingLineRichTemplate = false;

  /** DMSNSメッセージ設定拡張配列 */
  _dmSnsMsgSettingExList: DmSnsMsgSettingEx[] = [];

  /** リッチテンプレート各吹き出しの直前の種別とアンケートNo。同一配列の編集差分に使う */
  private _surveyInContentSnapshot: Array<{ bodyType: string; surveyNo: number | null }> = [];

  /** ラベル選択表示フラグ */
  labelAllSelect = true;

  /** 入力検証除外対象 (base) */
  private baseIgnoreRules = [
    'DmContentInfo#dmContentNumber',
    'DmContentInfo#dmExtractMenuCode',
    'DmContentInfo#dmInvitationMediaDiv',
    'DmContentInfo#dmContentName',
    'DmContentInfo#dmContentUseDiv',
    'DmContentInfo#dmRoutineCheckItemServiceNumInfoList',
    'DmContentInfo#mpDmRoutineCheckItemServiceNumInfoList',
    'DmContentInfo#mpVehicleInspItemServiceNum',
    'DmSnsMsgSetting#itemNumber',
    'FileManageInfo#fileManageId',
    'FileManageInfo#uploadFileName',
    'FileManageInfo#fileTypeDiv',
    'FileManageInfo#expireDateTime',
    'DmSnsMsgSettingDetail#itemNumber',
    'DmRoutineCheckItemServiceNumInfo#dmContentVcleCheckDiv',
    'DmRoutineCheckItemServiceNumInfo#itemServiceNum',
  ];

  /** 入力検証除外対象 (LINE リッチテンプレート以外の追加項目) */
  private notLineRithIgnoreRules = [
    'DmSnsMsgSettingDetail#dmSnsMsgTitle',
    'DmSnsMsgSettingDetail#dmSnsMsgBody',
    'DmSnsMsgSetting#lineMsgTemplateType',
  ];

  /** コンストラクタ */
  constructor(
    private service: SnsSendMessageModalService,
    private loadingService: BlLoadingService,
    private modalService: BlModalService,
    private dialogService: BlDialogService,
    private validatorService: BlValidatorService,
    private snsImageService: SnsImageService,
    private surveySettingResourceDisplay: SurveySettingResourceDisplayService,
  ) {
    super();
  }

  /** @override */
  defaultOption() {
    return { size: ModalSize.Large };
  }

  ngOnInit(): void {
    this.initSnsExtractCondition();
  }

  ngOnDestroy() {
    this.hideLoading();
    this.onDestroy$.emit();
  }

  getResults(): any {
    return {};
  }

  /** 初期表示データ取得 */
  onShown() {
    this.data.dmExtractMenuCode = [DmExtractMenuCode.Customize, DmExtractMenuCode.CouponIssue, DmExtractMenuCode.Other];
    this.service.getLoginUserInfo().subscribe(
      (res) => {
        this.data.organizationCode = res.organizationCode;
        // ログイン組織の情報を取得
        this.service.getOrganizationInfo(this.data.organizationCode).subscribe(
          organization => {
            this.isAbleToOtherOrgDataRef = organization.otherOrgDataReference ?
              organization.otherOrgDataReference : false;
            // LINE公式アカウント項目を設定
            this.setLineOfficialAccount();
          });
      }
    );

    // クーポン関連機能が使用可能か判定
    this.service.isAvailableCouponMangeService().subscribe((responses) => {
      this.isAvailableCouponMng = responses;
    });

    // Web予約サービスが使用可能か判定
    this.service.isAvailableWebReserveService().subscribe((responses) => {
      this.isAvailableWebReserveService = responses;
    });

    // アンケート基本サービスが使用可能か判定
    this.service.isAvailableSurveyService().subscribe((responses) => {
      this.isAvailableSurvey = responses;
    });

    // DM案内文情報取得
    this.service.dmContentSearch(this.data.dmExtractMenuCode, this.data.dmInvitationMediaDiv)
      .pipe(
        takeUntil(this.onDestroy$)
      )
      .subscribe((res) => {
        if (res.length > 0) {
          // 案内文の選択活性
          this.approachSelectDisabledFlg = false;
          this.dmContentInfoList = res;
          const contentInfoItems = [this.dmContentInfoDefaultItem];
          contentInfoItems.push(...res.map((extlist) => {
            if (extlist.initConditionDiv) {
              this.updateDmContentInfo(extlist);
            }
            return { code: extlist.dmContentNumber, name: extlist.dmContentName };
          }));
          this.contentInfoAlternativeList = contentInfoItems;

          if (!this._snsSendMessageInfo.dmContentInfo) {
            // 初期値が見つからない場合、空が選択されているとみなす
            this.updateDmContentInfo(null);
          }
        } else {
          // 案内文設定が1件もない場合、空が選択されているとみなす
          this.updateDmContentInfo(null);
        }
      },
      () => { // エラー時は空のプルダウン表示
      });
    // DM発行設定情報取得
    this.getDmIssueSetting();
    // DM案内文設定のバリデーション取得
    this.service.getDmContentInfoRules().subscribe((res) => {
      this.dmContentInfoRules = res;
      this.setValidate();
    });
  }
  /**
   * 「LINE公式アカウント」項目を設定
   */
  setLineOfficialAccount() {
    // LINE公式アカウント情報取得
    this.service.getSnsShopAccountSetting().subscribe(
      (res) => {
        this.snsShopAccountSettingList = res;
        this.lineOfficialAccountList = res.filter((info) => {
          if (this.data.dmInvitationMediaDiv === DmInvitationMediaDiv.Line && info.snsDiv === SnsDiv.LINE) {
            // ログイン組織に紐づくLINE公式アカウントか
            const isMyOrgSnsShopAccountSetting
              = _some(info.organizationCodeList, orgnizationCode => orgnizationCode === this.data.organizationCode);
            if (isMyOrgSnsShopAccountSetting) {
              // ログイン組織に紐づく公式アカウントを設定
              this._snsSendMessageInfo.snsLinkageId = info.snsLinkageId;
              this._snsSendMessageInfo.snsShopAccountName = info.snsShopAccountName;
            }
            if (this.isAbleToOtherOrgDataRef || isMyOrgSnsShopAccountSetting) {
              // ログイン組織が他組織データ参照可能な場合、
              // もしくはログイン組織に紐づくLINE公式アカウントの場合のみリストに追加
              return true;
            } else {
              return false;
            }
          }
          return false;
        })
          .map((info) => {
            return { code: info.snsLinkageId, name: info.snsShopAccountName };
          });
        if (this.isAbleToOtherOrgDataRef && _isNil(this._snsSendMessageInfo.snsLinkageId) && this.lineOfficialAccountList.length > 0) {
          // ログイン組織が本社拠点かつ自組織に紐づくLINE公式アカウントがない場合はLINE公式アカウント未選択の選択肢を追加する
          const tempLineAccountList = this.lineOfficialAccountList;
          this.lineOfficialAccountList = [];
          this.lineOfficialAccountList.push(
            { code: '', name: this.UNSELECTED_LINE_OFFICIAL_ACCOUNT_STR }, ...tempLineAccountList
          );
          this._snsSendMessageInfo.snsLinkageId = '';
          this._snsSendMessageInfo.snsShopAccountName = this.UNSELECTED_LINE_OFFICIAL_ACCOUNT_STR;
        }
        if (this.lineOfficialAccountList.length > 1) {
          this.hasMultipleLineAccount = true;
        }
      });
  }

  /**
   * LINE公式アカウント選択イベント
   * @param LINE公式アカウントコード
   */
  onChangeLineOfficialAccount(event) {
    const lineOfficialAccount = _find(this.lineOfficialAccountList, data => data.code === event);
    this._snsSendMessageInfo.snsLinkageId = lineOfficialAccount.code;
    this._snsSendMessageInfo.snsShopAccountName = lineOfficialAccount.name;
  }

  /**
   * 案内文選択イベント
   * @param 案内文番号
   */
  onChangeInfoItem(event) {
    const nextContent = _find(this.dmContentInfoList, data => data.dmContentNumber === event);
    const confirmSurveyChange = this.shouldConfirmSurveyChangeByContent(nextContent);
    // 案内文は切り替え、アンケートは確認が必要なときだけ後から反映する
    this.updateDmContentInfo(nextContent, !confirmSurveyChange);
    if (confirmSurveyChange) {
      this.confirmSurveyChangeByContent(nextContent);
    }
  }

  /**
   * 案内文の作成イベント
   */
  addApproach(event) {
    // DM案内文設定のバリデーション取得
    const entity: IDmContentInfo = {
      dmExtractMenuCode: DmExtractMenuCode.Customize,
      dmInvitationMediaDiv: this.data.dmInvitationMediaDiv
    };
    const modalData = {
      editMode: EditMode.Add,
      rules: this.dmContentInfoRules,
      entity: entity,
      isGrandsonMode: true
    };

    // DM案内文設定モーダル表示
    this.modalService
      .show(ApproachAnnounceDetailComponent, { data: modalData }).pipe(
        takeUntil(this.onDestroy$)
      )
      .subscribe(modalRef => {
        if (modalRef.reason === ModalReason.Done) {
          _delay(() => {
            // DM案内文情報取得
            this.service.dmContentSearch(this.data.dmExtractMenuCode, this.data.dmInvitationMediaDiv)
              .pipe(
                takeUntil(this.onDestroy$)
              )
              .subscribe((response) => {
                if (response.length > 0) {
                  // 案内文の選択活性
                  this.approachSelectDisabledFlg = false;
                  this.dmContentInfoList = response;
                  this.contentInfoAlternativeList = response.map((extlist) => {
                    return { code: extlist.dmContentNumber, name: extlist.dmContentName };
                  });
                  this.updateDmContentInfo(response[0]);
                }
              },
                () => { // エラー時は空のプルダウン表示
                });
          }, 1000);
        }
        modalRef.hide(event.target);
      });
  }

  /**
   * DM発行設定情報取得
   */
  private getDmIssueSetting() {
    this.service.dmIssueSearch(this.data.organizationCode)
      .pipe(
        takeUntil(this.onDestroy$)
      )
      .subscribe((res) => {
        this.dmIssueSetting = res;

        // 「SMS送信」且つコンソールＩＤが設定されていない場合はinfoダイアログを表示する
        if (_isNil(res.smsConsoleId) && this.data.dmInvitationMediaDiv === DmInvitationMediaDiv.Sms) {
          const s = this.dialogService.info('本機能はSMSオプションご契約後、ご利用できます。')
            .subscribe(ref => {
              ref.hide();
              s.unsubscribe();
            });
        }
      });
  }

  /**
   * クーポン発行ガイドを開く
   */
  onShowTicketGuide(event, num: number) {
    this.modalService.show(TicketIssueGuideComponent,
      { data: { organizationCode: this.data.organizationCode } }
    ).subscribe(modalRef => {
      if (modalRef.reason === ModalReason.Done) {
        // 返却値がなければ何もしない
        if (_isNil(modalRef.getResults())) {
          modalRef.hide(event.target);
          return;
        }
        const selectedTicketCode = modalRef.getResults().ticketCode;
        // 変更前と同じチケットなら何もしない
        if (this.ticketSettingList[num].ticketCode === selectedTicketCode) {
          modalRef.hide(event.target);
          return;
        }
        // 既に使用済みコードの場合はダイアログで確認する
        const isDuplicateTicket = _some(this.ticketSettingList, ticket => ticket.ticketCode === selectedTicketCode);
        if (isDuplicateTicket) {
          this.dialogService.confirm('このクーポンは既に選択されています。\n重複しますが、このまま追加してよろしいですか？').subscribe(
            dialogRef => {
              dialogRef.hide();
              if (dialogRef.reason === ModalReason.Done) {
                this.ticketSettingList[num] = modalRef.getResults();
              }
            }
          );
        } else {
          this.ticketSettingList[num] = modalRef.getResults();
        }
      }
      modalRef.hide(event.target);
    });
  }

  /**
   * クーポン 削除ボタンクリック
   */
  onClickDelete(num: number) {
    this.ticketSettingList.splice(num, 1);
  }

  /**
   * クーポン 追加ボタンクリック
   */
  onClickEdit() {
    this.ticketSettingList.push({});
  }

  /**
   * 送信するアンケート変更イベントハンドラ
   * @param survey 選択したアンケート
   */
  onChangeSurvey(survey: ISurveyGuideLabelValue) {
    // 送信設定のアンケートは案内文本体とは別に保持する（approach ConfirmSendMessageModal と同様）
    this._selectedSurvey = survey || {};
    this.cacheSelectedSurvey(survey);
  }

  /**
   * 送信するアンケート項目を表示するか
   */
  private isSurveySendable(): boolean {
    return this.isAvailableSurvey;
  }

  /**
   * 案内文に紐づくアンケート番号を送信するアンケートへ反映する
   * 案内文にアンケートが紐づいていない場合は、今の選択を残す
   * アンケート名が不明な場合はアンケート番号でアンケート設定を検索して表示する
   * @param dmContentInfo DM案内文情報
   */
  private applyLinkedSurveyToSendSetting(dmContentInfo: IDmContentInfo) {
    if (!this.isSurveySendable()) {
      this._selectedSurvey = {};
      return;
    }
    const surveyNo = this.findLinkedSurveyNo(dmContentInfo);
    if (_isNil(surveyNo) || surveyNo === 0) {
      return;
    }
    const surveyName = this.findLinkedSurveyName(dmContentInfo, surveyNo);
    this._selectedSurvey = {
      surveyNo,
      surveyName: surveyName || '',
    };
    if (_isEmpty(surveyName)) {
      this.resolveSelectedSurveyName(surveyNo);
    } else {
      this.surveySettingResourceDisplay.cacheSurveyName(surveyNo, surveyName);
    }
  }

  /**
   * 案内文変更で、現在のアンケートが別のアンケートへ変わるか
   * 変更前または変更後が未選択の場合は確認不要
   * @param content 変更後の案内文
   */
  private shouldConfirmSurveyChangeByContent(content: IDmContentInfo): boolean {
    if (!this.isSurveySendable() || _isNil(content)) {
      return false;
    }
    const currentSurveyNo = this._selectedSurvey ? this._selectedSurvey.surveyNo : null;
    const nextSurveyNo = this.findLinkedSurveyNo(content);
    if (!this.isValidSurveyNo(currentSurveyNo) || !this.isValidSurveyNo(nextSurveyNo)) {
      return false;
    }
    return currentSurveyNo !== nextSurveyNo;
  }

  /**
   * 案内文のアンケートへ変更するか確認し、承諾されたときだけ反映する
   * @param content 選択中の案内文
   */
  private confirmSurveyChangeByContent(content: IDmContentInfo): void {
    const currentSurveyNo = this._selectedSurvey.surveyNo;
    const nextSurveyNo = this.findLinkedSurveyNo(content);
    const contentNumber = content.dmContentNumber;
    ObservableForkJoin(
      this.resolveSurveyName(currentSurveyNo, this._selectedSurvey.surveyName),
      this.resolveSurveyName(nextSurveyNo),
    ).pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(
      names => {
        if (_isNil(this._snsSendMessageInfo.dmContentInfo)
          || this._snsSendMessageInfo.dmContentInfo.dmContentNumber !== contentNumber) {
          return;
        }
        if (_isNil(this._selectedSurvey) || this._selectedSurvey.surveyNo !== currentSurveyNo) {
          return;
        }
        const message = DmSurveyUtil.getSurveyChangeMessage(names[0], names[1]);
        this.dialogService.confirm(message).subscribe(ref => {
          ref.hide();
          if (ref.reason !== ModalReason.Done) {
            return;
          }
          if (_isNil(this._snsSendMessageInfo.dmContentInfo)
            || this._snsSendMessageInfo.dmContentInfo.dmContentNumber !== contentNumber) {
            return;
          }
          this.applyLinkedSurveyToSendSetting(this._snsSendMessageInfo.dmContentInfo);
          this.applySelectedSurveyToSendInfo();
        });
      },
      error => {
        if (error.status === HttpStatus.Unauthorized) {
          throw error;
        }
      }
    );
  }

  /**
   * アンケート名を返す。分かっている名称はキャッシュし、無いときはサービス経由で取得する
   * @param surveyNo アンケート番号
   * @param knownName 分かっているアンケート名
   */
  private resolveSurveyName(surveyNo: number, knownName?: string): Observable<string> {
    if (this.hasSurveyName(knownName)) {
      this.surveySettingResourceDisplay.cacheSurveyName(surveyNo, knownName);
      return ObservableOf(knownName);
    }
    return this.surveySettingResourceDisplay.getSurveyNameBySurveyNo(surveyNo);
  }

  /**
   * アンケート案内文件名・本文を返す。分かっている値はキャッシュし、無いときはサービス経由で取得する
   * @param surveyNo アンケート番号
   */
  private resolveSurveyContent(surveyNo: number): Observable<{ surveyContentTitle: string; surveyContentBody: string }> {
    const knownTitle = (this._selectedSurvey && this._selectedSurvey.surveyNo === surveyNo)
      ? this._selectedSurvey.surveyContentTitle
      : null;
    const knownBody = (this._selectedSurvey && this._selectedSurvey.surveyNo === surveyNo)
      ? this._selectedSurvey.surveyContentBody
      : null;
    if (!_isEmpty(knownTitle) || !_isEmpty(knownBody)) {
      const surveyContentTitle = knownTitle || '';
      const surveyContentBody = knownBody || '';
      this.surveySettingResourceDisplay.cacheSurveyContent(surveyNo, surveyContentTitle, surveyContentBody);
      return ObservableOf({ surveyContentTitle, surveyContentBody });
    }
    return this.surveySettingResourceDisplay.getSurveyContentBySurveyNo(surveyNo);
  }

  /**
   * 選択中アンケートの名称・案内文をキャッシュする
   * 案内文件名も本文も空のときは、取得済みの案内文キャッシュを空で上書きしない
   * @param survey 選択したアンケート
   */
  private cacheSelectedSurvey(survey: ISurveyGuideLabelValue): void {
    if (_isNil(survey) || !this.isValidSurveyNo(survey.surveyNo)) {
      return;
    }
    if (this.hasSurveyName(survey.surveyName)) {
      this.surveySettingResourceDisplay.cacheSurveyName(survey.surveyNo, survey.surveyName);
    }
    if (!_isEmpty(survey.surveyContentTitle) || !_isEmpty(survey.surveyContentBody)) {
      this.surveySettingResourceDisplay.cacheSurveyContent(
        survey.surveyNo,
        survey.surveyContentTitle || '',
        survey.surveyContentBody || ''
      );
    }
  }

  /**
   * アンケート番号として扱う値か
   * @param surveyNo アンケート番号
   */
  private isValidSurveyNo(surveyNo: number | null | undefined): boolean {
    return !_isNil(surveyNo) && surveyNo !== 0;
  }

  /**
   * 表示できるアンケート名があるか
   * @param surveyName アンケート名
   */
  private hasSurveyName(surveyName: string | null | undefined): boolean {
    return !_isNil(surveyName) && _trim(surveyName) !== '';
  }

  /**
   * 案内文に紐づくアンケート番号を取得する
   * リッチ版はアンケート吹き出しの番号を使う。案内文直下の surveyNo だけを引き継ぐと、クーポンのみでも吹き出し超過になる
   * @param dmContentInfo DM案内文情報
   * @return アンケート番号
   */
  private findLinkedSurveyNo(dmContentInfo: IDmContentInfo): number | null {
    if (_isNil(dmContentInfo)) {
      return null;
    }
    // 吹き出し設定を持つのはリッチテンプレートなので、その案内文自身で判定する
    if (this.isLineRichTemplate(dmContentInfo)) {
      const setting = _find(dmContentInfo.dmSnsMsgSettingList, item =>
        item.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.Survey && !_isNil(item.surveyNo)
      );
      return setting ? setting.surveyNo : null;
    }
    return !_isNil(dmContentInfo.surveyNo) ? dmContentInfo.surveyNo : null;
  }

  /**
   * 案内文に保持されているアンケート名を取得する
   * @param dmContentInfo DM案内文情報
   * @param surveyNo アンケート番号
   * @return アンケート名
   */
  private findLinkedSurveyName(dmContentInfo: IDmContentInfo, surveyNo: number): string {
    if (_isNil(dmContentInfo) || _isNil(dmContentInfo.dmSnsMsgSettingList)) {
      return '';
    }
    const setting = _find(dmContentInfo.dmSnsMsgSettingList, item =>
      item.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.Survey && item.surveyNo === surveyNo
    );
    if (_isNil(setting)) {
      return '';
    }
    return (<IDmSnsMsgSettingEx>setting).surveyName || '';
  }

  /**
   * アンケート名が不明な送信アンケートを、アンケート番号で検索して表示へ反映する
   * @param surveyNo アンケート番号
   */
  private resolveSelectedSurveyName(surveyNo: number) {
    this.surveySettingResourceDisplay.getSurveySettingBySurveyNo(surveyNo).pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(surveySetting => {
      if (_isNil(this._selectedSurvey) || this._selectedSurvey.surveyNo !== surveyNo) {
        return;
      }
      if (!_isEmpty(this._selectedSurvey.surveyName)) {
        return;
      }
      if (_isNil(surveySetting) || _isEmpty(surveySetting.surveyName)) {
        return;
      }
      this._selectedSurvey = {
        surveyNo,
        surveyName: surveySetting.surveyName,
        surveyContentTitle: surveySetting.surveyContentTitle,
        surveyContentBody: surveySetting.surveyContentBody,
      };
    });
  }

  /**
   * 選択中アンケートを送信情報へ反映する
   * 送信するアンケートが未選択の場合、プレビュー・送信のアンケート番号は空にする
   */
  private applySelectedSurveyToSendInfo() {
    const surveyNo = this._selectedSurvey ? this._selectedSurvey.surveyNo : null;
    this._snsSendMessageInfo.surveyNo = this.isSurveySendable() && !_isNil(surveyNo) && surveyNo !== 0
      ? surveyNo
      : null;
  }

  /**
   * アンケート必須な案内文なのに、案内文・送信設定のどちらにもアンケートが未設定か
   */
  private isSurveyRequiredButNotConfigured(): boolean {
    if (!this.isSurveyRequiredContent()) {
      return false;
    }
    return !this.hasSurveyOnContent() && !this.hasSurveyOnSendSetting();
  }

  /**
   * アンケート送信タイプ、またはアンケート吹き出しがある案内文か
   */
  private isSurveyRequiredContent(): boolean {
    const content = this._snsSendMessageInfo.dmContentInfo;
    const extractMenuCode = content && !_isNil(content.dmExtractMenuCode)
      ? content.dmExtractMenuCode
      : this._snsSendMessageInfo.dmExtractMenuCode;
    if (extractMenuCode === DmExtractMenuCode.Survey) {
      return true;
    }
    return this.hasSurveyInContent();
  }

  /**
   * 案内文側にアンケートが設定されているか
   */
  private hasSurveyOnContent(): boolean {
    const content = this._snsSendMessageInfo.dmContentInfo;
    if (!content) {
      return false;
    }
    if (!_isNil(content.surveyNo)) {
      return true;
    }
    // 編集中の吹き出し状態も見る
    if (_some(this._dmSnsMsgSettingExList, ex =>
      ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.Survey
      && !_isNil(ex.survey) && !_isNil(ex.survey.surveyNo)
    )) {
      return true;
    }
    return _some(content.dmSnsMsgSettingList, setting =>
      setting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.Survey && !_isNil(setting.surveyNo)
    );
  }

  /**
   * 送信設定にアンケートが設定されているか
   */
  private hasSurveyOnSendSetting(): boolean {
    return !_isNil(this._selectedSurvey) && !_isNil(this._selectedSurvey.surveyNo);
  }

  /**
   * 案内文にアンケート吹き出しがあるか
   */
  private hasSurveyInContent(): boolean {
    if (_some(this._dmSnsMsgSettingExList, ex =>
      ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.Survey
    )) {
      return true;
    }
    const content = this._snsSendMessageInfo.dmContentInfo;
    return _some(content && content.dmSnsMsgSettingList, setting =>
      setting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.Survey
    );
  }

  /**
   * リッチ版LINEテンプレート対象か
   * @param dmContentInfo 判定する案内文。未指定のときは選択中の案内文
   */
  private isLineRichTemplate(dmContentInfo?: IDmContentInfo): boolean {
    const hasTargetContent = !_isNil(dmContentInfo);
    const content = hasTargetContent ? dmContentInfo : this._snsSendMessageInfo.dmContentInfo;
    // 引数なしのときは、一斉送信モーダル上でリッチ編集中なら対象
    if (!hasTargetContent && this._isSettingLineRichTemplate) {
      return true;
    }
    const extractMenuCode = content && !_isNil(content.dmExtractMenuCode)
      ? content.dmExtractMenuCode
      : (hasTargetContent ? null : this._snsSendMessageInfo.dmExtractMenuCode);
    const invitationMediaDiv = content && content.dmInvitationMediaDiv
      ? content.dmInvitationMediaDiv
      : this.data.dmInvitationMediaDiv;
    return DmLineUtil.isLineRichTemplateMenu(invitationMediaDiv, extractMenuCode);
  }

  /**
   * 吹き出し件数の判定に使う案内文の吹き出し設定
   * リッチ編集中で吹き出しがあるときは、画面上の編集結果を使う
   */
  private getLineRichContentSettingList(): IDmSnsMsgSetting[] | null {
    if (this._isSettingLineRichTemplate && this._dmSnsMsgSettingExList.length > 0) {
      return _map(this._dmSnsMsgSettingExList, ex => ex.dmSnsMsgSetting);
    }
    const content = this._snsSendMessageInfo.dmContentInfo;
    return content ? content.dmSnsMsgSettingList : null;
  }

  /**
   * 送信設定にクーポンが1件以上あるか
   */
  private hasCouponOnSendSetting(): boolean {
    return _some(this.ticketSettingList, ticketSetting => !_isNil(ticketSetting.ticketCode));
  }

  /**
   * リッチテンプレートで、案内文に必要な吹き出しが上限を超えるか
   */
  private isLineRichTemplateMessageOverflow(): boolean {
    if (!this.isLineRichTemplate()) {
      return false;
    }
    return DmLineUtil.isLineRichTemplateBalloonOverflow(
      this.getLineRichContentSettingList(),
      this.hasCouponOnSendSetting(),
      this.hasSurveyOnSendSetting()
    );
  }

  /**
   * クーポン発行ガイド（抽出条件）を開く
   */
  onShowTicketGuideForSnsExtractCondition(event, num: number) {
    this.modalService.show(TicketIssueGuideComponent,
      { data: { organizationCode: this.data.organizationCode } }
    ).subscribe(modalRef => {
      if (modalRef.reason === ModalReason.Done) {
        // 返却値がなければ何もしない
        if (_isNil(modalRef.getResults())) {
          modalRef.hide(event.target);
          return;
        }
        const selectedTicketCode = modalRef.getResults().ticketCode;
        // 変更前と同じチケットなら何もしない
        if (this._snsExtractCondition.ticketSettingList[num].ticketCode === selectedTicketCode) {
          modalRef.hide(event.target);
          return;
        }
        // 既に使用済みコードの場合はダイアログで確認する
        const isDuplicateTicket = _some(this._snsExtractCondition.ticketSettingList, ticket => ticket.ticketCode === selectedTicketCode);
        if (isDuplicateTicket) {
          this.dialogService.confirm('このクーポンは既に選択されています。\n重複しますが、このまま追加してよろしいですか？').subscribe(
            dialogRef => {
              dialogRef.hide();
              if (dialogRef.reason === ModalReason.Done) {
                this._snsExtractCondition.ticketSettingList[num] = modalRef.getResults();
                this.changeUnregisteredFriendsFlg();
              }
            }
          );
        } else {
          this._snsExtractCondition.ticketSettingList[num] = modalRef.getResults();
          this.changeUnregisteredFriendsFlg();
        }
      }
      modalRef.hide(event.target);
    });
  }

  /**
   * クーポン（抽出条件） 削除ボタンクリック
   */
  onClickDeleteForSnsExtractCondition(num: number) {
    this._snsExtractCondition.ticketSettingList.splice(num, 1);
  }

  /**
   * クーポン（抽出条件） 追加ボタンクリック
   */
  onClickEditForSnsExtractCondition() {
    this._snsExtractCondition.ticketSettingList.push({});
  }

  /**
   * SNS抽出条件情報の初期化処理
   */
  private initSnsExtractCondition() {
    this._snsExtractCondition.unregisteredFriendsFlg = false;
    this._snsExtractCondition.snsFriendRegistDateFrom = null;
    this._snsExtractCondition.snsFriendRegistDateTo = null;
    this._snsExtractCondition.snsLastMessageReceiveDateTimeFrom = null;
    this._snsExtractCondition.snsLastMessageReceiveDateTimeFrom = null;
    this._snsExtractCondition.labelCodeList = [];
    this._snsExtractCondition.labelNameList = [];
    this._snsExtractCondition.searchNotTicketFlg = false;
    this._snsExtractCondition.ticketSettingList = [{}];
    this._snsExtractCondition.applyStartDate = null;
    this._snsExtractCondition.applyEndDate = null;
    this._snsExtractCondition.ticketIssueDate = null;
    this._snsExtractCondition.applyEndDateStart = null;
    this._snsExtractCondition.applyEndDateEnd = null;
  }

  /**
   * ラベルとクーポン関連のSNS抽出条件情報を初期化する
   */
  private initSnsExtractConditionToLabelAndTicket() {
    this._snsExtractCondition.labelCodeList = [];
    this._snsExtractCondition.labelNameList = [];
    this._snsExtractCondition.searchNotTicketFlg = false;
    this._snsExtractCondition.ticketSettingList = [{}];
    this._snsExtractCondition.applyStartDate = null;
    this._snsExtractCondition.applyEndDate = null;
    this._snsExtractCondition.ticketIssueDate = null;
    this._snsExtractCondition.applyEndDateStart = null;
    this._snsExtractCondition.applyEndDateEnd = null;
  }

  /**
   * SNS送信メッセージ情報の最新化処理
   */
  private updateSnsSendMessageInfo() {
    this._snsSendMessageInfo.snsDiv = SnsDiv.LINE;
    if (!_isNil(this._snsSendMessageInfo.dmContentInfo)) {
      this._snsSendMessageInfo.dmExtractMenuCode = this._snsSendMessageInfo.dmContentInfo.dmExtractMenuCode;
    }
    this._snsSendMessageInfo.dmExtractMenuDiv = DmExtractMenuDiv.Other;
    this._snsSendMessageInfo.lineMsgTemplateType = LineMsgTemplateType.FreeExtract;
    this._snsSendMessageInfo.ticketSettingList = this.ticketSettingList;
    this._snsSendMessageInfo.ticketCodeList = [];
    this._snsSendMessageInfo.ticketNameList = [];
    _forEach(this.ticketSettingList, ticketSetting => {
      if (!_isNil(ticketSetting.ticketCode)) {
        this._snsSendMessageInfo.ticketCodeList.push(ticketSetting.ticketCode);
        this._snsSendMessageInfo.ticketNameList.push(ticketSetting.ticketName);
      }
    });
    this.applySelectedSurveyToSendInfo();
    this._snsSendMessageInfo.snsShopAccountSetting = this.findSelectedSnsShopAccountSetting();
    this._snsSendMessageInfo.snsExtractCondition = this._snsExtractCondition;
  }

  /**
   * プレビュー・送信で同期するアンケートの案内文件名・本文を解決する
   * アンケート選択中は、追加・変更どちらでも件名・本文を載せる
   * 確認モーダルは別サービスのため、ここでキャッシュ付きで取得して送信情報へ載せる
   */
  private preparePreviewSurveyContent(): Observable<null> {
    const info = this._snsSendMessageInfo;
    info.surveyContentTitle = '';
    info.surveyContentBody = '';
    const surveyNo = info.surveyNo;
    if (!this.isSurveySendable() || !this.isValidSurveyNo(surveyNo)) {
      return ObservableOf(null);
    }
    return this.resolveSurveyContent(surveyNo).pipe(
      map(content => {
        info.surveyContentTitle = content.surveyContentTitle;
        info.surveyContentBody = content.surveyContentBody;
        return null;
      })
    );
  }

  /**
   * 選択中のLINE公式アカウントのSNS店舗アカウント設定を取得する
   */
  private findSelectedSnsShopAccountSetting(): ISnsShopAccountSetting | null {
    const snsLinkageId = this._snsSendMessageInfo.snsLinkageId;
    if (_isEmpty(snsLinkageId)) {
      return null;
    }
    return _find(this.snsShopAccountSettingList, info => info.snsLinkageId === snsLinkageId) || null;
  }

  /**
   * 顧客未登録の友だちのみフラグの変更処理
   */
  private changeUnregisteredFriendsFlg() {
    if (this._snsExtractCondition.unregisteredFriendsFlg === true) {
      // 顧客未登録の友だちのみフラグがONの場合は無視
      return;
    }
    if (this.hasSendTargetConditionByLabelOrTicket()) {
      let dialogDispMsg = 'ラベルの条件を設定した為、\n「顧客未登録の友だちのみ」の条件をONに変更します。';
      if (this.isAvailableCouponMng) {
        dialogDispMsg = 'ラベルまたはクーポンの条件を設定した為、\n「顧客未登録の友だちのみ」の条件をONに変更します。';
      }
      // ラベルかクーポンの条件がセットされている
      this.dialogService.info(dialogDispMsg).subscribe(
        ref => {
          this._snsExtractCondition.unregisteredFriendsFlg = true;
          ref.hide();
        }
      );
    }
  }

  /**
   * SNS抽出条件のチケット情報が指定されているかのチェック
   * @returns 指定有無
   */
  private hasTicketSettingCondition(): boolean {
    let hasFlg = false;
    _forEach(this._snsExtractCondition.ticketSettingList, ticketSetting => {
      if (!_isNil(ticketSetting.ticketCode)) {
        hasFlg = true;
      }
    });
    return hasFlg;
  }

  /**
   * モーダルウィンドウ終了処理
   * @param reason
   */
  dismiss(reason: ModalReason): void {
    // 確定以外の場合はそのまま通知
    if (reason !== ModalReason.Done) {
      this.emitDismissEvent(reason);
      return;
    }
    // バリデーションチェック
    if (!this.validate()) {
      return;
    }
    this.dispSnsSendMessageConfirmModal(reason);
  }

  /**
   * SNS一斉送信確認モーダルを起動する
   */
  private dispSnsSendMessageConfirmModal(reason: ModalReason) {
    this.loadingService.show();
    // SNS送信メッセージ情報の更新
    this.updateSnsSendMessageInfo();
    // 画像アップロードと、プレビュー用アンケート案内文の解決
    ObservableForkJoin(
      this.preparePreviewSurveyContent(),
      this.uploadImage(this._snsSendMessageInfo.dmContentInfo)
    ).pipe(mergeMap(() => {
      _defer(() => {
        this.loadingService.hide();
      });

      // SNS一斉送信確認モーダルの起動
      return this.modalService.show(SnsSendMessageConfirmModalComponent, {
        data: {
          snsSendMessageInfo: this._snsSendMessageInfo,
        },
      });
    })).subscribe(modalRef => {
      modalRef.hide();
      if (modalRef.reason === ModalReason.Done) {
        this.emitDismissEvent(reason);
      }
    });
  }

  /** 顧客未登録の友だちのみチェックボックスがクリックされたとき */
  onClickUnregisteredFriendsFlg(event: Event) {
    if (this._snsExtractCondition.unregisteredFriendsFlg) {
      // ON⇒OFFのとき
      if (this.hasSendTargetConditionByLabelOrTicket()) {
        // クリックイベントの伝搬を抑止
        event.preventDefault();
        event.stopPropagation();
        let dialogDispMsg = 'ラベルの条件が設定されている為、チェックをOFFに変更出来ません。\n'
          + '「ラベル」の条件をクリアしますか？';
        if (this.isAvailableCouponMng) {
          dialogDispMsg = 'ラベルまたはクーポンの条件が設定されている為、チェックをOFFに変更出来ません。\n'
            + '「ラベル」「クーポン」の条件をクリアしますか？';
        }
        // ラベルかクーポンの条件が設定されてるとき
        this.dialogService.confirm(dialogDispMsg).subscribe(
          modalRef => {
            if (modalRef.reason === ModalReason.Done) {
              // ラベルとクーポンの条件をクリアする
              this.initSnsExtractConditionToLabelAndTicket();
              this._snsExtractCondition.unregisteredFriendsFlg = false;
              // 条件クリア済の通知
              let clearedDialogMsg = '「ラベル」の条件をクリアしました。';
              if (this.isAvailableCouponMng) {
                clearedDialogMsg = '「ラベル」「クーポン」の条件をクリアしました。';
              }
              this.dialogService.info(clearedDialogMsg).subscribe(
                infoDialogRef => {
                  infoDialogRef.hide();
                }
              );
            }
            modalRef.hide();
          }
        );
      }
    }
  }

  /** 顧客未登録の友だちのみチェックボックスが変更されたとき */
  onChangeUnregisteredFriendsFlg() {
    this._snsExtractCondition.unregisteredFriendsFlg = !this._snsExtractCondition.unregisteredFriendsFlg;
  }

  /** SNS友達登録年月日(開始) */
  onChangeSnsFriendRegistDateFrom(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.snsFriendRegistDateFrom = event;
    } else {
      this._snsExtractCondition.snsFriendRegistDateFrom = null;
    }
  }
  /** SNS友達登録年月日(終了) */
  onChangeSnsFriendRegistDateTo(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.snsFriendRegistDateTo = event;
    } else {
      this._snsExtractCondition.snsFriendRegistDateTo = null;
    }
  }
  /** SNS最終メッセージ受信日時(開始) */
  onChangeSnsLastMessageReceiveDateTimeFrom(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.snsLastMessageReceiveDateTimeFrom = event;
    } else {
      this._snsExtractCondition.snsLastMessageReceiveDateTimeFrom = null;
    }
  }
  /** SNS最終メッセージ受信日時(終了) */
  onChangeSnsLastMessageReceiveDateTimeTo(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.snsLastMessageReceiveDateTimeTo = event;
    } else {
      this._snsExtractCondition.snsLastMessageReceiveDateTimeTo = null;
    }
  }

  /**
   * ラベル変更
   * @param $event
   */
  onChangeLabel($event) {
    const labelCodeList = _cloneDeep(this._snsExtractCondition.labelCodeList);
    const labelNameList = _cloneDeep(this._snsExtractCondition.labelNameList);
    _forEach($event, value => {
      if (value.selected) {
        labelCodeList.push(value.label.labelCode);
        labelNameList.push(value.label.labelName);
      } else {
        _remove(labelCodeList, (v) => v === value.label.labelCode);
        _remove(labelNameList, (v) => v === value.label.labelName);
      }
    });
    this._snsExtractCondition.labelCodeList = labelCodeList;
    this._snsExtractCondition.labelNameList = labelNameList;
    this.changeUnregisteredFriendsFlg();
  }

  /** チケット不所持フラグ */
  onChangeSelectedSearchNotTicketFlg() {
    this._snsExtractCondition.searchNotTicketFlg = !this._snsExtractCondition.searchNotTicketFlg;
    this.changeUnregisteredFriendsFlg();
  }


  /** チケット有効期限(開始) */
  onChangeApplyStartDate(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.applyStartDate = event;
    } else {
      this._snsExtractCondition.applyStartDate = null;
    }
    this.changeUnregisteredFriendsFlg();
  }

  /** チケット有効期限(終了) */
  onChangeApplyEndDate(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.applyEndDate = event;
    } else {
      this._snsExtractCondition.applyEndDate = null;
    }
    this.changeUnregisteredFriendsFlg();
  }

  /** チケット発行日 */
  onChangeTicketIssueDate(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.ticketIssueDate = event;
    } else {
      this._snsExtractCondition.ticketIssueDate = null;
    }
    this.changeUnregisteredFriendsFlg();
  }

  /** チケット有効期限終了日(開始) */
  onChangeApplyEndDateStart(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.applyEndDateStart = event;
    } else {
      this._snsExtractCondition.applyEndDateStart = null;
    }
    this.changeUnregisteredFriendsFlg();
  }

  /** チケット有効期限終了日(終了) */
  onChangeApplyEndDateEnd(event: string) {
    if (event !== DateTimeUtils.initial.iso8601.date) {
      this._snsExtractCondition.applyEndDateEnd = event;
    } else {
      this._snsExtractCondition.applyEndDateEnd = null;
    }
    this.changeUnregisteredFriendsFlg();
  }

  /**
   * @ignore
   * 検索条件パネル条件クリアイベント
   * @param _event イベント
   */
  onSearchConditonClear(_event: Event) {
    this.initSnsExtractCondition();
  }

  /**
   * ラベルかクーポン関連の抽出条件がセットされているかを判定する
   * @returns true:セットされている、false:セットされていない
   */
  private hasSendTargetConditionByLabelOrTicket() {
    return (this._snsExtractCondition.labelCodeList.length > 0
      || this._snsExtractCondition.searchNotTicketFlg === true
      || this.hasTicketSettingCondition()
      || this._snsExtractCondition.applyStartDate != null
      || this._snsExtractCondition.applyEndDate != null
      || this._snsExtractCondition.ticketIssueDate != null
      || this._snsExtractCondition.applyEndDateStart != null
      || this._snsExtractCondition.applyEndDateEnd != null);
  }

  /**
   * バリデーションチェック
   * @returns true:OK、false:NG
   */
  private validate(): boolean {
    if (!this.blFormGroup.validate(true)) {
      return false;
    }
    if (_isNil(this._snsSendMessageInfo.snsLinkageId)) {
      this.dialogService.warn('LINE公式アカウントを登録してください。').subscribe(
        ref => ref.hide()
      );
      return false;
    }
    if (_isNil(this._snsSendMessageInfo.dmContentInfo)) {
      this.dialogService.warn('案内文を選択してください。').subscribe(
        ref => ref.hide()
      );
      return false;
    }
    if (_isNil(this._snsSendMessageInfo.snsSendTitle) || this._snsSendMessageInfo.snsSendTitle.length === 0) {
      this.dialogService.warn('一斉送信名称を入力してください。').subscribe(
        ref => ref.hide()
      );
      return false;
    }
    if (this._isSettingLineRichTemplate &&
      this._snsSendMessageInfo.dmContentInfo.dmInvitationMediaDiv === DmInvitationMediaDiv.Line) {
      const notSelectedImage = _some(
        _filter(this._dmSnsMsgSettingExList, v => v.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.Image),
        v => _isNil(v.file) && (_isNil(v.dmSnsMsgSetting.dmSnsMsgDispFile) || _isNil(v.dmSnsMsgSetting.dmSnsMsgDispFile.fileManageId))
      );
      if (notSelectedImage) {
        this.dialogService.warn('画像を設定してください。').subscribe(ref => ref.hide());
        return false;
      }
    }
    // アンケート必須な案内文なのに未設定の場合は送信しない
    if (this.isSurveyRequiredButNotConfigured()) {
      this.dialogService.warn(this.SURVEY_NOT_SET_ERR_MSG).subscribe(ref => ref.hide());
      return false;
    }
    // 必要な吹き出しが上限を超える場合は送信しない
    if (this.isLineRichTemplateMessageOverflow()) {
      this.dialogService.warn(LINE_RICH_TEMPLATE_BALLOON_OVERFLOW_MESSAGE).subscribe(ref => ref.hide());
      return false;
    }
    return true;
  }

  /**
   * DM案内文情報を更新する
   * @param dmContentInfo DM案内文情報(デフォルト項目が選択された場合)
   * @param applySurvey 案内文に紐づくアンケートを送信アンケートへ反映するか
   */
  private updateDmContentInfo(dmContentInfo: IDmContentInfo | null | undefined, applySurvey = true) {
    let snsSendTitle = '';
    if (_isNil(dmContentInfo)) {
      dmContentInfo = {
        dmContentNumber: 0,
        dmExtractMenuCode: DmExtractMenuCode.Other,
        dmContentName: '',
        dmContentTitle: '',
        dmContentBody: '',
      };
    } else {
      snsSendTitle = dmContentInfo.dmContentName + ' ' + DateTimeUtils.today();
    }

    this._snsSendMessageInfo.dmContentInfo = dmContentInfo;
    this._snsSendMessageInfo.dmContentNumber = dmContentInfo.dmContentNumber;
    this._snsSendMessageInfo.dmExtractMenuCode = dmContentInfo.dmExtractMenuCode;
    this._snsSendMessageInfo.dmContentName = dmContentInfo.dmContentName;
    this._snsSendMessageInfo.dmContentTitle = dmContentInfo.dmContentTitle;
    this._snsSendMessageInfo.dmContentBody = dmContentInfo.dmContentBody;
    this._snsSendMessageInfo.snsSendTitle = snsSendTitle;
    this.setSettingLineRichTemplate(this._snsSendMessageInfo);
    if (applySurvey) {
      this.applyLinkedSurveyToSendSetting(dmContentInfo);
    }
    this.applySelectedSurveyToSendInfo();
    this.setValidate();
  }

  /** バリデーション設定 */
  private setValidate() {
    const formGroupOption: FormGroupOption = {
      ignoreRules: this._isSettingLineRichTemplate ? this.baseIgnoreRules :
        this.baseIgnoreRules.concat(this.notLineRithIgnoreRules),
    };
    this.blFormGroup = this.validatorService.getBlFormGroup(this.dmContentInfoRules, formGroupOption);
    this.applyRequiredInputDiv();
    this.blFormGroup.reset();
  }

  /** 入力必須項目を設定 */
  private applyRequiredInputDiv() {
    const validator = this.blFormGroup.getValidator('DmContentInfo#dmContentBody');
    if (validator) {
      const rule = _cloneDeep(validator.getRule());
      rule.requiredInputDiv = !this._isSettingLineRichTemplate;
      validator.updateRule(rule);
    }
  }

  /**
   * リッチ版LINEテンプレートを設定するかを判定する
   * @param snsMessageInfo 送信メッセージ情報
   * @returns true:リッチ版LINEテンプレートを設定する、 false:リッチ版LINEテンプレートを設定しない
   */
  private setSettingLineRichTemplate(snsMessageInfo: ISnsSendMessageInfo) {
    this._isSettingLineRichTemplate =
      snsMessageInfo.dmExtractMenuCode === DmExtractMenuCode.Customize ||
      snsMessageInfo.dmExtractMenuCode === DmExtractMenuCode.Other;
  }

  /**
   * DMSNSメッセージ設定が更新された時に発火
   * @param dmSnsMsgSettingExList DMSNSメッセージ設定拡張配列
   */
  onUpdateDmSnsMsgSetting(dmSnsMsgSettingExList: DmSnsMsgSettingEx[]) {
    // 案内文の読み込みや吹き出しの追加・移動は配列が作り直される。アンケート選択の変更は同一配列を書き換える
    const isInPlaceEdit = dmSnsMsgSettingExList === this._dmSnsMsgSettingExList;
    this._dmSnsMsgSettingExList = dmSnsMsgSettingExList;
    if (!_isNil(this._snsSendMessageInfo.dmContentInfo)) {
      this._snsSendMessageInfo.dmContentTitle = this._snsSendMessageInfo.dmContentInfo.dmContentTitle;
      this._snsSendMessageInfo.dmContentBody = this._snsSendMessageInfo.dmContentInfo.dmContentBody;
      this._snsSendMessageInfo.dmContentInfo.dmSnsMsgSettingList = dmSnsMsgSettingExList.map(v => v.dmSnsMsgSetting);
      if (isInPlaceEdit) {
        this.feedbackSurveyFromRichTemplate(dmSnsMsgSettingExList);
      }
    }
    this._surveyInContentSnapshot = this.toRichSurveySnapshot(dmSnsMsgSettingExList);
  }

  /**
   * リッチテンプレートのアンケート吹き出しの選択を、送信するアンケートへ反映する
   * アンケートから別タイプの吹き出しへ変更した場合は送信するアンケートを維持する
   * @param list 更新後の吹き出し
   */
  private feedbackSurveyFromRichTemplate(list: DmSnsMsgSettingEx[]) {
    const previous = this._surveyInContentSnapshot;
    const count = Math.max(previous.length, list ? list.length : 0);
    for (let i = 0; i < count; i++) {
      const prev = previous[i];
      const next = list ? list[i] : null;
      const nextType = next && next.dmSnsMsgSetting ? next.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv : null;
      if (_isNil(prev) || prev.bodyType !== DmSnsMsgBodyTypeDiv.Survey || nextType !== DmSnsMsgBodyTypeDiv.Survey) {
        continue;
      }
      const nextNo = this.toRichTemplateSurveyNo(next);
      if (prev.surveyNo === nextNo) {
        continue;
      }
      this.applyRichTemplateSurvey(next, nextNo);
    }
  }

  /**
   * リッチテンプレートで選んだアンケートを送信するアンケートへ入れる
   * 選択を空にした場合は送信するアンケートも空にする
   * @param ex アンケート吹き出し
   * @param surveyNo アンケートNo。未選択はnull
   */
  private applyRichTemplateSurvey(ex: DmSnsMsgSettingEx, surveyNo: number | null) {
    if (_isNil(surveyNo)) {
      this._selectedSurvey = {};
      return;
    }
    const survey = ex ? ex.survey : null;
    if (survey && survey.surveyNo === surveyNo && !_isEmpty(survey.surveyName)) {
      this.cacheSelectedSurvey(survey);
      this._selectedSurvey = {
        surveyNo,
        surveyName: survey.surveyName,
        surveyContentTitle: survey.surveyContentTitle,
        surveyContentBody: survey.surveyContentBody,
      };
      return;
    }
    this._selectedSurvey = { surveyNo };
    this.resolveSelectedSurveyName(surveyNo);
  }

  /**
   * 吹き出しの種別とアンケートNoを値で控える
   * @param list 吹き出し
   */
  private toRichSurveySnapshot(list: DmSnsMsgSettingEx[]): Array<{ bodyType: string; surveyNo: number | null }> {
    return _map(list || [], ex => ({
      bodyType: ex && ex.dmSnsMsgSetting ? ex.dmSnsMsgSetting.dmSnsMsgBodyTypeDiv : null,
      surveyNo: this.toRichTemplateSurveyNo(ex),
    }));
  }

  /**
   * 吹き出しのアンケートNoを返す。未選択と0はnull
   * @param ex 吹き出し
   */
  private toRichTemplateSurveyNo(ex: DmSnsMsgSettingEx): number | null {
    const surveyNo = ex && ex.dmSnsMsgSetting ? ex.dmSnsMsgSetting.surveyNo : null;
    if (_isNil(surveyNo) || surveyNo === 0) {
      return null;
    }
    return surveyNo;
  }

  /**
   * 画像をアップロードします。
   */
  private uploadImage(entity: IDmContentInfo): Observable<boolean[]> {
    return this.snsSendMessageContent.saveAttachments().pipe(
      tap((dmSnsMsgSettingList) => {
        dmSnsMsgSettingList.forEach((dmSnsMsgSetting) => {
          const target = entity.dmSnsMsgSettingList.find(v => v.itemNumber === dmSnsMsgSetting.itemNumber);
          if (!_isNil(target)) {
            if (dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.FlexMessage) {
              target.dmSnsMsgSettingDetailList[0].dmSnsMsgDispFile = dmSnsMsgSetting.dmSnsMsgSettingDetailList[0].dmSnsMsgDispFile;
            } else {
              target.dmSnsMsgDispFile = dmSnsMsgSetting.dmSnsMsgDispFile;
            }
          }
        });
      }),
      mergeMap(() => {
        if (_isEmpty(entity.dmSnsMsgSettingList)) {
          return ObservableOf(null);
        }
        return ObservableForkJoin(entity.dmSnsMsgSettingList.map((dmSnsMsgSetting, index) => {
          const file = this._dmSnsMsgSettingExList[index].file;
          if (_isNil(file)) {
            return ObservableOf(true);
          }
          return this.snsImageService.uploadImage(new BlImage(file), this._snsSendMessageInfo.snsLinkageId)
            .pipe(map((response: IImageUploadInfo) => {
              if (_isNil(response)) {
                return true;
              }
              if (dmSnsMsgSetting.dmSnsMsgBodyTypeDiv === DmSnsMsgBodyTypeDiv.FlexMessage) {
                entity.dmSnsMsgSettingList[index].dmSnsMsgSettingDetailList[0].snsImageId = response.imageId;
                entity.dmSnsMsgSettingList[index].dmSnsMsgSettingDetailList[0].snsImageUrl = response.imageUrl;
              } else {
                entity.dmSnsMsgSettingList[index].snsImageId = response.imageId;
                entity.dmSnsMsgSettingList[index].snsImageUrl = response.imageUrl;
                entity.dmSnsMsgSettingList[index].snsThumbnailImageId = response.thumbnailId;
                entity.dmSnsMsgSettingList[index].snsThumbnailImageUrl = response.thumbnailUrl;
              }
              return true;
            }));
        }));
      })
    );
  }

}



// WEBPACK FOOTER //
// ./src/app/feature/sns-send-message-modal/sns-send-message-modal.component.ts